/**
 * §6.5's two absolute dates, from `Intl` and with **no English literal in the module**.
 *
 * ⚠️ **`onDateTime` has a second reader since TASK-080** — §6.6/§6.7's screen, for the same fact this
 * card renders on an interview row: when a conversation was held. It is imported from here rather than
 * copied, because a second `Intl` call with its own options is a second date grammar for one fact, and
 * the two screens would drift apart at the first change to either.
 *
 * The card states instants rather than durations: *"date opened"* on a consideration, the
 * conversation's date on an interview row, and the moment an erasure happened on the anonymisation
 * banner. None of them is a relative time — §6.3's *"2 days ago"* pair lives in
 * `Candidates/updatedAt.ts` and stays there, because a list is scanned for recency while a card is
 * read for the record.
 *
 * They come from the platform's locale data rather than from the catalogue for the reason
 * `updatedAt.ts` records: a month name, an ordering and a separator are per-locale, and a
 * hand-written `"opened {{d}}/{{m}}/{{y}}"` key would carry one locale's grammar into every language
 * added later. §11's actual property — no hardcoded copy in the source — is kept, and the sentence
 * *around* the date ("Opened …") is an i18n key with the formatted date interpolated into it.
 */

/** A day, in the reader's own locale: `2026-07-01T09:00:00Z` → `1 Jul 2026`. */
export function onDate(iso: string, locale: string): string {
  const instant = Date.parse(iso);
  if (Number.isNaN(instant)) return '';

  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(instant);
}

/**
 * A day **and** a time, for the two instants where the time of day is part of the fact: when a
 * conversation was held (an interview row) and when a person's data was erased (ADR 0009's banner,
 * which is the only record that the erasure ever happened).
 */
export function onDateTime(iso: string, locale: string): string {
  const instant = Date.parse(iso);
  if (Number.isNaN(instant)) return '';

  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
    instant,
  );
}

/**
 * A time of day and **no date** — §6.6's *"Saved 14:32"*, the one instant in the product that is
 * always today and always minutes ago (TASK-083's save indicator).
 *
 * ⚠️ **`hourCycle: 'h23'`, which is the one place these helpers overrule the locale, and §6.6 is why.**
 * The indicator is *"(mono time)"* in a sticky header beside a page title: `HH:MM` is two tabular
 * numerals and a colon at a fixed width, while `en-US`'s own short time would render *"2:32 PM"* —
 * wider, variable, and no longer the mono device §3 asks numerals to be. The **value** still comes from
 * the platform's locale data (the digits, the separator), so nothing about a translation is decided
 * here; the 24-hour clock is a design decision recorded in `DESIGN.md` §6.6 rather than a locale bug.
 *
 * The instant it formats is always the server's — `InterviewOut.updated_at`, the save timestamp — and
 * never `Date.now()`, which would let the indicator claim a save at a moment the server never wrote.
 */
export function atTimeOfDay(iso: string, locale: string): string {
  const instant = Date.parse(iso);
  if (Number.isNaN(instant)) return '';

  return new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(instant);
}
